import { Box, Tag, TagCloseButton, TagProps, Tooltip } from "@chakra-ui/react";
import React from "react";

export function SelectedItemTag<T extends BaseGenericType>({
  selectedItem,
  index,
  getSelectedItemProps,
  size,
  isDisabled,
  onClose,
  renderTagContent,
  isInvalid,
  invalidItemMessage,
}: SelectedItemTagProps<T>): React.ReactElement {
  const tag = (
    <ClosableTag
      tagProps={
        isInvalid
          ? {
              variant: "subtle",
              backgroundColor: undefined,
              color: undefined,
              colorScheme: "red",
              boxShadow: "inset 0 0 0 2px var(--chakra-colors-red-500)",
            }
          : undefined
      }
      size={size}
      isDisabled={isDisabled}
      onClose={onClose}
    >
      {renderTagContent(selectedItem)}
    </ClosableTag>
  );

  return (
    <Box
      key={`selected-item-${index}`}
      {...getSelectedItemProps({
        selectedItem,
        index,
      })}
      display="inline-block"
      maxW="100%"
    >
      {isInvalid && invalidItemMessage !== undefined ? (
        <Tooltip label={invalidItemMessage} hasArrow placement="top">
          <Box as="span" display="inline-flex" maxW="100%">
            {tag}
          </Box>
        </Tooltip>
      ) : (
        tag
      )}
    </Box>
  );
}

interface BaseGenericType {
  id: string;
  group?: string;
  [key: string]: any;
}

type ClosableTagProps = {
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
  size: "sm" | "md" | "lg";
  isDisabled?: boolean;
  tagProps?: TagProps;
} & React.PropsWithChildren;

const ClosableTag = ({
  children,
  onClose,
  size,
  isDisabled = false,
  tagProps,
}: ClosableTagProps): React.ReactElement => {
  return (
    <Tag
      size={size}
      borderRadius="6px"
      variant="solid"
      colorScheme="gray"
      backgroundColor="blackAlpha.200"
      color="black"
      paddingX={size === "lg" ? 3 : 2}
      paddingY={size === "sm" ? "2px" : size === "md" ? "6px" : "4px"}
      fontSize={size === "lg" ? "md" : "sm"}
      {...tagProps}
    >
      {children}
      {!isDisabled && <TagCloseButton onClick={onClose} />}
    </Tag>
  );
};

interface SelectedItemTagProps<T extends BaseGenericType> {
  selectedItem: T;
  index: number;
  getSelectedItemProps: (options: {
    selectedItem: T;
    index: number;
  }) => Record<string, unknown>;
  size: "sm" | "md" | "lg";
  isDisabled: boolean;
  onClose: (event: React.MouseEvent<HTMLButtonElement>) => void;
  renderTagContent: (item: T) => React.ReactElement;
  isInvalid?: boolean;
  invalidItemMessage?: string | null;
}
